import { LogOut, ArrowLeftRight } from "lucide-react";
import { connect } from "react-redux";
import { Link, useHistory, useLocation } from "react-router-dom";
import { createStructuredSelector } from "reselect";

import { setCurrentDistro } from "../../redux/distro/distro.actions";
import { setCurrentUser } from "../../redux/user/user.actions";
import { selectCurrentUser } from "../../redux/user/user.selectors";
import { selectCurrentDistro } from "../../redux/distro/distro.selector";
import { isClientOnlyForDistro, withoutRestrictedLinks } from "../../utils/firmAccess";
import { advisorSidebarData, ownerSidebarData } from "./sidebarData";

const Sidebar = ({
    currentUser,
    currentDistro,
    setCurrentUser,
    setCurrentDistro,
    showSidebar,
}) => {
    const history = useHistory();
    const location = useLocation();

    const baseSidebarData =
        currentUser.role === "owner" ? ownerSidebarData : advisorSidebarData;
    const sidebarData = isClientOnlyForDistro(currentUser, currentDistro)
        ? baseSidebarData.map((section) => ({ ...section, links: withoutRestrictedLinks(section.links) }))
        : baseSidebarData;

    return (
        <>
            {/* sidebar - desktop: fixed left, mobile: slide down under header */}
            <aside
                className={`fixed w-60 bg-white/80 backdrop-blur-xl border-r border-gray-200/60 overflow-y-auto transition-all duration-300 ease-out
                    lg:top-16 lg:bottom-0 lg:left-0 lg:translate-y-0 lg:z-50
                    max-lg:top-0 max-lg:pt-16 max-lg:left-0 max-lg:right-0 max-lg:w-full max-lg:bottom-auto max-lg:border-r-0 max-lg:border-b max-lg:rounded-b-2xl max-lg:shadow-lg max-lg:bg-white max-lg:backdrop-blur-none max-lg:max-h-screen max-lg:overflow-y-auto max-lg:z-40
                    ${showSidebar ? "max-lg:translate-y-0 max-lg:opacity-100" : "max-lg:-translate-y-full max-lg:opacity-0 max-lg:pointer-events-none"}
                `}
            >
                <div className="py-6 px-3">
                    {sidebarData.map((item, idx) => {
                        return (
                            <div key={idx} className="mb-6">
                                <h3 className="px-3 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                    {item.section}
                                </h3>
                                <nav className="space-y-1">
                                    {item.links.map((link) => {
                                        const isActive = location.pathname === link.path ||
                                            (link.path.includes('?') && location.pathname + location.search === link.path);
                                        const IconComponent = link.icon;
                                        return !link.externalLink ? (
                                            <Link
                                                key={link.title}
                                                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                                                    isActive
                                                        ? "bg-primary/10 text-primary"
                                                        : "text-gray-600 hover:bg-gray-100/80 hover:text-gray-900"
                                                }`}
                                                to={link.path}
                                            >
                                                {IconComponent && (
                                                    <span className={`w-5 h-5 flex items-center justify-center ${isActive ? "text-primary" : "text-gray-500"}`}>
                                                        <IconComponent className="w-5 h-5" />
                                                    </span>
                                                )}
                                                <span>{link.title}</span>
                                            </Link>
                                        ) : (
                                            <Link
                                                key={link.title}
                                                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100/80 hover:text-gray-900 transition-all duration-200"
                                                to={{ pathname: link.path }}
                                                target="_blank"
                                            >
                                                {IconComponent && (
                                                    <span className="w-5 h-5 flex items-center justify-center text-gray-500">
                                                        <IconComponent className="w-5 h-5" />
                                                    </span>
                                                )}
                                                <span>{link.title}</span>
                                            </Link>
                                        );
                                    })}
                                </nav>
                                {/* role switch + logout buttons - mobile only (shown after Marketing section) */}
                                {item.section === "Marketing" && (
                                    <>
                                        {currentUser.hasDualRole && (
                                            <button
                                                className="lg:hidden w-full flex items-center gap-3 px-3 py-3 mt-4 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
                                                onClick={() => {
                                                    const newRole = currentUser.role === 'owner' ? 'advisor' : 'owner';
                                                    setCurrentDistro(null);
                                                    setCurrentUser({ ...currentUser, role: newRole });
                                                    history.push(newRole === 'owner' ? '/user/dashboard' : '/user/profile');
                                                }}
                                            >
                                                <ArrowLeftRight className="w-5 h-5" />
                                                <span className="font-medium">Switch to {currentUser.role === 'owner' ? 'Advisor' : 'Firm'} Portal</span>
                                            </button>
                                        )}
                                        <button
                                            className={`lg:hidden w-full flex items-center gap-3 px-3 py-3 ${currentUser.hasDualRole ? '' : 'mt-4'} text-red-600 hover:bg-red-50 rounded-xl transition-colors`}
                                            onClick={() => {
                                                setCurrentDistro(null);
                                                setCurrentUser();
                                            }}
                                        >
                                            <LogOut className="w-5 h-5" />
                                            <span className="font-medium">Logout</span>
                                        </button>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            </aside>
        </>
    );
};

const mapStateToProps = createStructuredSelector({
    currentUser: selectCurrentUser,
    currentDistro: selectCurrentDistro,
});

const mapDispatchToProps = (dispatch) => ({
    setCurrentUser: (data) => dispatch(setCurrentUser(data)),
    setCurrentDistro: (data) => dispatch(setCurrentDistro(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Sidebar);
